import ACTIONS from "./actions";

const initialState = {
  isLoading: false,
  isError: false,
  hasFetched: false,
  data: null,
  // command related states
  commandLoading: false,
  commandError: false,
  lastCommandResponse: null,
};

/**
 * Normalises the raw charger config API response into a flat shape
 * that the Overview tab can render directly.
 *
 * Key derivations performed here:
 *
 *  1. Field aliases — some API versions use different key names:
 *       partNum       → partNumber
 *       fwVersion     → firmwareVersion
 *       maxCurrent    → ratedCurrent
 *       gridType      → phaseMode
 *
 *  2. status — synthesised from isConnected / isFaulted when absent:
 *       isFaulted === true            → "FAULTED"
 *       isConnected === true          → "NORMAL"
 *       otherwise                     → "OFFLINE"
 *
 *  3. vendorConfig — the API returns this as an array of { key, value }
 *     pairs. These are flattened into a lookup map (vcMap) and the
 *     following fields are derived:
 *
 *     a. operatingVoltage
 *          vcMap.operatingVoltage (raw number) → "<value>V"
 *
 *     b. activeConnection / isConnected
 *          Source: vcMap.netConfig
 *          Format: JSON-stringified array where each element is itself a
 *          JSON-stringified interface object:
 *            [
 *              "{\"interface_name\":\"eth0\",\"connectionStatus\":0,...}",
 *              "{\"interface_name\":\"mlan0\",\"connectionStatus\":1,...}",
 *              "{\"interface_name\":\"wwan0\",\"connectionStatus\":0,...}"
 *            ]
 *          Logic: find the first interface with connectionStatus === 1, then:
 *            eth*  + connected  → activeConnection = "Ethernet"
 *            mlan* + connected  → activeConnection = "WiFi (<SSID>)"
 *                                  SSID comes from vcMap.wifiSettings.SSID
 *            other + connected  → activeConnection = interface_name (raw)
 *            none connected     → activeConnection = "Disconnected"
 *          The matched interface object is stored as netConfigDetails for
 *          the key:value detail view in the Overview tab.
 *
 *     c. wifiConfigDetails / wifiConfig
 *          Source: vcMap.wifiSettings (JSON string)
 *          wifiConfigDetails — full parsed object for key:value detail view
 *          wifiConfig        — human-readable summary: "<status> (<SSID>)"
 *
 *     d. cellularSettings
 *          Source: vcMap.cellularSettings (JSON string)
 *          Stored as a parsed object for key:value detail view.
 */
function normaliseConfig(raw) {
  if (!raw || typeof raw !== "object") return raw;
  const data = { ...raw };

  // ── 1. Field aliases ──
  if (data.partNum !== undefined && data.partNumber === undefined) {
    data.partNumber = data.partNum;
  }
  if (data.fwVersion !== undefined && data.firmwareVersion === undefined) {
    data.firmwareVersion = data.fwVersion;
  }
  if (data.maxCurrent !== undefined && data.ratedCurrent === undefined) {
    data.ratedCurrent = data.maxCurrent;
  }
  if (data.gridType !== undefined && data.phaseMode === undefined) {
    data.phaseMode = data.gridType;
  }

  // ── 2. Synthesise status ──
  if (data.status === undefined) {
    if (data.isFaulted) data.status = "FAULTED";
    else if (data.isConnected) data.status = "NORMAL";
    else data.status = "OFFLINE";
  }

  // ── 3. Flatten vendorConfig array into a key→value map ──
  if (Array.isArray(data.vendorConfig)) {
    const vcMap = {};
    data.vendorConfig.forEach(item => {
      if (item.key && item.value !== undefined) vcMap[item.key] = item.value;
    });

    // ── 3a. Operating Voltage ──
    if (vcMap.operatingVoltage && data.operatingVoltage === undefined) {
      data.operatingVoltage = `${vcMap.operatingVoltage}V`;
    }

    // ── 3b. activeConnection / isConnected ──
    // vcMap.netConfig is a JSON string whose value is an array of
    // further JSON-stringified interface objects (double-encoded).
    // Each interface object has:
    //   interface_name   — "eth0" (Ethernet), "mlan0" (WiFi), "wwan0" (Cellular)
    //   connectionStatus — 1 = connected, 0 = disconnected
    try {
      if (vcMap.netConfig) {
        const parsed = JSON.parse(vcMap.netConfig);
        // Normalise: handle both array-of-strings (real API) and single object (legacy)
        const interfaces = Array.isArray(parsed)
          ? parsed.map(entry => (typeof entry === "string" ? JSON.parse(entry) : entry))
          : [parsed];

        // Pick the first interface that reports connectionStatus === 1
        const active = interfaces.find(i => Number(i.connectionStatus) === 1);

        if (active) {
          const iface = active.interface_name || "";
          if (iface.startsWith("eth")) {
            // Wired Ethernet
            data.activeConnection = "Ethernet";
            data.isConnected = true;
          } else if (iface.startsWith("mlan")) {
            // WiFi — SSID is in vcMap.wifiSettings (separate vendorConfig key)
            let ssid = "";
            try {
              if (vcMap.wifiSettings) {
                const ws = JSON.parse(vcMap.wifiSettings);
                ssid = ws.SSID || "";
              }
            } catch {
              /* ignore wifiSettings parse failure */
            }
            data.activeConnection = ssid ? `WiFi (${ssid})` : "WiFi";
            data.isConnected = true;
          } else {
            // Unknown interface type — show raw name
            data.activeConnection = iface;
            data.isConnected = true;
          }
          // Expose the active interface details for the Network Config key:value row
          data.netConfigDetails = active;
        } else {
          // No interface has connectionStatus === 1
          data.activeConnection = "Disconnected";
          data.isConnected = false;
          // Still expose the first interface for context in the detail row
          if (interfaces.length > 0) data.netConfigDetails = interfaces[0];
        }
      }
    } catch {
      /* ignore netConfig parse errors */
    }

    // ── 3c. WiFi Config ──
    // vcMap.wifiSettings is a JSON string with keys: SSID, status, wifiMode, connectionStatus
    try {
      if (vcMap.wifiSettings) {
        const ws = JSON.parse(vcMap.wifiSettings);
        // Full object for the WiFi Config key:value detail row
        data.wifiConfigDetails = ws;
        // Human-readable summary for the WiFi Config string row
        if (data.wifiConfig === undefined) {
          data.wifiConfig = ws.status || "-";
          if (ws.SSID) data.wifiConfig += ` (${ws.SSID})`;
        }
      }
    } catch {
      /* ignore wifiSettings parse errors */
    }

    // ── 3d. Cellular Settings ──
    // vcMap.cellularSettings is a JSON string with keys: signalStrength, connectionStatus, status, network
    try {
      if (vcMap.cellularSettings) {
        data.cellularSettings = JSON.parse(vcMap.cellularSettings);
      }
    } catch {
      /* ignore cellularSettings parse errors */
    }
  }

  // ── 4. Flatten chargeLevelDetails.defaultChargeLevel ──
  if (data.chargeLevelDetails?.defaultChargeLevel !== undefined && data.defaultChargeLevel === undefined) {
    data.defaultChargeLevel = data.chargeLevelDetails.defaultChargeLevel;
  }

  // ── 5. Parse externalMeter string into separate fields ──
  // Format: "Enabled, Connected, (Meter Type: p1, Serial Number: 4530...)"
  //      or: "Disabled"
  if (data.externalMeter !== undefined && data.externalMeter !== null) {
    const raw = String(data.externalMeter);
    const parts = raw.split(",").map(s => s.trim());
    data.extMeterStatus = parts[0] || raw;
    data.extMeterConnection = parts.length > 1 ? parts[1].replace(/^\(/, "").trim() : null;

    const meterTypeMatch = raw.match(/Meter Type:\s*([^,)]+)/i);
    data.extMeterType = meterTypeMatch ? meterTypeMatch[1].trim() : null;

    const serialMatch = raw.match(/Serial Number:\s*([^,)]+)/i);
    data.extMeterSerialNumber = serialMatch ? serialMatch[1].trim() : null;
  }

  // ── 6. Flatten wiringConfiguration map to string ──
  if (data.wiringConfiguration !== undefined && data.wiringConfiguration !== null) {
    if (typeof data.wiringConfiguration === "object" && !Array.isArray(data.wiringConfiguration)) {
      const entries = Object.entries(data.wiringConfiguration);
      data.wiringConfiguration = entries.length > 0 ? entries.map(([k, v]) => `${k}: ${v}`).join(", ") : "-";
    } else if (typeof data.wiringConfiguration !== "string") {
      data.wiringConfiguration = String(data.wiringConfiguration);
    }
  }

  // ── 6. Normalise gatewayConnectivityDetails ──
  if (Array.isArray(data.gatewayConnectivityDetails)) {
    data.gatewayConnectivityDetails = data.gatewayConnectivityDetails.map(gw => {
      const normalized = { ...gw };
      // Convert lastConnTime from epoch ms to readable date string
      if (normalized.lastConnTime && typeof normalized.lastConnTime === "number") {
        normalized.lastConnTime = new Date(normalized.lastConnTime).toISOString();
      }
      return normalized;
    });
  }

  return data;
}

function chargerConfigReducer(state, action) {
  switch (action.type) {
    case ACTIONS.RESET_CHARGER_CONFIG:
      return { ...initialState };
    case ACTIONS.FETCH_CHARGER_CONFIG_INIT:
      return {
        ...state,
        isLoading: true,
        isError: false,
      };
    case ACTIONS.FETCH_CHARGER_CONFIG_SUCCESS:
      return {
        ...state,
        isLoading: false,
        isError: false,
        hasFetched: true,
        data: normaliseConfig(action.payload),
      };
    case ACTIONS.FETCH_CHARGER_CONFIG_FAILURE:
      return {
        ...state,
        isLoading: false,
        isError: true,
        hasFetched: true,
      };
    case ACTIONS.SEND_OCPP_COMMAND_INIT:
      return {
        ...state,
        commandLoading: true,
        commandError: false,
        lastCommandResponse: null,
      };
    case ACTIONS.SEND_OCPP_COMMAND_SUCCESS:
      return {
        ...state,
        commandLoading: false,
        commandError: false,
        lastCommandResponse: action.payload,
      };
    case ACTIONS.SEND_OCPP_COMMAND_FAILURE:
      return {
        ...state,
        commandLoading: false,
        commandError: true,
      };
    default:
      return state;
  }
}

export { initialState };
export default chargerConfigReducer;
